import React, { useContext, useState, useRef, useEffect } from "react"
import { useQuery, useLazyQuery } from "@apollo/client"
import { Link, useNavigate } from "react-router-dom"
import { DataTable } from "primereact/datatable"
import { Column } from "primereact/column"
import { SplitButton } from "primereact/splitbutton"
import { Toast } from "primereact/toast"
import { FilterMatchMode, FilterOperator } from "primereact/api"
import _ from "lodash"
import { MultiSelect } from "primereact/multiselect"
import { UserContext } from "../../../context/userContext"
import LoaderFullPage from "../../../components/Loaders"
import { DOCUMENTS_STAFF } from "../../DocumentManagement/queries"
import { GET_STAFF_ID_BY_MEMBERSHIP_ID, GET_STAFF_NAME } from "../queries"

import { parseDate } from "../../../helpers/dateNormalization"
import { FirebaseContext } from "../../../firebase"

import styles from "../../../styles/Page-Engagements.module.css"
import StaffSidebar from "../../../components/sidebars/StaffSidebar"
import StaffProfileBox from "../../Staff/components/StaffProfileBox"
import { navigateWithOrigin } from "../../../helpers/navigation"

export default function StaffDocuments() {
  const [data, setData] = useState(null)
  const [error, setError] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const firebase = useContext(FirebaseContext)

  const userCtx = useContext(UserContext)
  const navigate = useNavigate()
  const toast = useRef(null)
  // adding some text to compile again prod. Remove if you see this.
  const allowedRoles = [0, 1, 2, 3] // Agrega roles permitidos
  const isSupervisor = userCtx.session.user.staff_role === 4
  const staff_membership_id = userCtx.current_staff_membership_id

  const { getStorage, ref, getDownloadURL } = firebase.storage

  // Define filters for DataTable
  const [filters] = useState({
    date: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
    },
    document_title: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
    },
    document_type: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.IN }],
    },
    location: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    created_by: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    staffName: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
  })
  const getDownloadURLFromStorage = async (path) => {
    const storage = getStorage()
    const fileRef = ref(storage, path)
    try {
      return await getDownloadURL(fileRef)
    } catch (error) {
      console.log(error)
      return null
    }
  }
  const formatDate = (value) => {
    return value.toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
  }

  const dateBodyTemplate = (rowData) => {
    return formatDate(rowData.date)
  }

  const [
    getStaffName,
    { data: staffNameData, loading: staffNameLoading, error: staffNameError },
  ] = useLazyQuery(GET_STAFF_NAME, {
    fetchPolicy: "network-only",
    onError: () => setError(true),
  })
  // Fetch staff_id and organization_id using staff_membership_id
  const {
    data: staffData,
    loading: staffLoading,
    error: staffError,
  } = useQuery(GET_STAFF_ID_BY_MEMBERSHIP_ID, {
    variables: { staff_membership_id },
    fetchPolicy: "network-only",
    skip: !staff_membership_id,
    onError: () => setError(true),
  })

  // Query to fetch staff documents
  const { loading, refetch } = useQuery(DOCUMENTS_STAFF, {
    variables: {
      idTemplate: "document_record_staff",
      organization_id: staffData?.staffDataByStaffMembershipID?.organization_id,
      staff_id: staffData?.staffDataByStaffMembershipID?.staff_id,
    },
    fetchPolicy: "network-only",
    skip:
      !staffData?.staffDataByStaffMembershipID?.staff_id ||
      !staffData?.staffDataByStaffMembershipID?.organization_id,
    onCompleted: async (data) => {
      if (!data.documentRecords || data.documentRecords.length === 0) {
        console.log("NO DATA")
        setData([])
        return
      }

      const parsedData = JSON.parse(data.documentRecords.data)
      const formattedData = _.map(parsedData, (blocks) => {
        const formBlock = _.find(blocks, { block_name: "form" })
        const documentBlock = _.find(blocks, {
          block_name: "document__document_record__staff",
        })
        const staffId =
          documentBlock?.values?.document_staff__staff_id_selector?.key || null

        if (staffId === staffData?.staffDataByStaffMembershipID?.staff_id) {
          const downloadUrl = getDownloadURLFromStorage(
            documentBlock.values.document_staff_file
          )
          const locationObject = formBlock.values.form__location_ids[0] || {}
          const location = {
            label: locationObject.label || "",
            key: locationObject.key || "",
          }

          // call getStaffName lazy query to get staff name
          getStaffName({
            variables: {
              staff_id: staffId,
              organization_id: formBlock.organization_id,
            },
          })

          const staffName = staffNameData?.getStaffName?.data || ""
          //(allowedRoles.includes(userCtx.session.user.staff_role) ||isSupervisor)
          let action
          allowedRoles.includes(userCtx.session.user.staff_role) || isSupervisor
            ? (action = (
                <SplitButton
                  label="View"
                  onClick={async () => {
                    // Obtén la URL de descarga
                    const downloadUrl = await getDownloadURLFromStorage(
                      documentBlock.values.document_staff_file
                    )

                    if (!downloadUrl) {
                      return toast.current.show({
                        severity: "error",
                        summary: "The reference to the File was not found.",
                        detail: "Please reupload the File by clicking Edit.",
                      })
                    }

                    // Obtén el nombre del archivo
                    const getFilename = () => {
                      const splitPath = _.split(
                        documentBlock.values.document_staff_file,
                        "/"
                      )
                      return splitPath[splitPath.length - 1]
                    }
                    const filename = getFilename()

                    // Descarga el archivo
                    const xhr = new XMLHttpRequest()
                    xhr.responseType = "blob"
                    xhr.onload = () => {
                      const blob = xhr.response
                      if (blob.size === 0) {
                        console.error("El Blob está vacío o corrupto.")
                        return
                      }
                      const link = document.createElement("a")
                      link.href = URL.createObjectURL(blob)
                      link.download = filename
                      link.click()
                    }

                    xhr.onerror = () => {
                      console.error(
                        "Error al descargar el archivo:",
                        xhr.statusText
                      )
                      toast.current.show({
                        severity: "error",
                        summary: "Error al descargar el archivo.",
                        detail: "Por favor, inténtalo de nuevo más tarde.",
                      })
                    }

                    xhr.open("GET", downloadUrl)
                    xhr.send()
                  }}
                  model={[
                    {
                      label: "Edit",
                      command: () => {
                        navigate(
                          `/form/${formBlock.idTemplate}/1/${formBlock.idStamp}`
                        )
                      },
                    },
                    {
                      label: "View Document Upload Session",
                      command: async () => {
                        await userCtx.changeParticipant(
                          formBlock.participant_id
                        )
                        navigateWithOrigin(
                          navigate,
                          `/form/view/${formBlock.idTemplate}/${formBlock.idStamp}`
                        )
                      },
                    },
                    {
                      label: "Delete",
                      command: async () => {
                        await handleDelete(formBlock.idStamp)
                        refetch()
                      },
                    },
                  ]}
                />
              ))
            : (action = (
                <SplitButton
                  label="View"
                  onClick={() => {
                    if (!downloadUrl) {
                      return toast.current.show({
                        severity: "error",
                        summary: "The reference to the File was not found.",
                        detail: "Please reupload the File by clicking Edit.",
                      })
                    }
                    const getFilename = () => {
                      const splitPath = _.split(
                        documentBlock.values.document_participant_file,
                        "/"
                      )
                      return splitPath[splitPath.length - 1]
                    }
                    const filename = getFilename()
                    const xhr = new XMLHttpRequest()
                    xhr.responseType = "blob"
                    xhr.onload = () => {
                      const blob = xhr.response
                      const link = document.createElement("a")
                      link.href = URL.createObjectURL(blob)
                      link.download = filename
                      link.click()
                    }
                    xhr.open("GET", downloadUrl)
                    xhr.send()
                  }}
                  model={[
                    {
                      label: "View Document Upload Session",
                      command: async () => {
                        await userCtx.changeParticipant(
                          formBlock.participant_id
                        )
                        navigateWithOrigin(
                          navigate,
                          `/form/view/${formBlock.idTemplate}/${formBlock.idStamp}`
                        )
                      },
                    },
                  ]}
                />
              ))
          return {
            date: parseDate(formBlock.values.date_created),
            location: location.label,
            staffName,
            document_title: documentBlock.values.document_staff_name,
            document_type: documentBlock.values.document_staff_type,
            created_by: formBlock.values.staff_full_name || "N/A",
            updated_by: formBlock.values.staff_full_name || "N/A",
            edit: action,
          }
        } else {
          return null // filter documents that don't belong to the current
        }
      }).filter(Boolean) // remove null values
      setData(formattedData)
    },
    onError: () => setError(true),
  })

  // Delete document handler
  const handleDelete = async (idStamp) => {
    try {
      setSubmitting(true)
      // Add your delete logic here (like an API call)
      toast.current.show({
        severity: "success",
        summary: "Deleted",
        detail: "Document deleted successfully",
      })
      refetch() // Re-fetch documents after deletion
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete document",
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (staffLoading || loading || submitting) return <LoaderFullPage />

  if (staffError || error)
    return <div>Error loading documents. Please try again later.</div>

  // Filter options for document type
  const documentTypeOptions = [
    { label: "Certificates or Licenses", value: "Certificates or Licenses" },
    { label: "Training Documents", value: "Training Documents" },
    { label: "Personnel Files", value: "Personnel Files" },
    { label: "Other", value: "Other" },
  ]

  const docTypeFilterTemplate = (options) => {
    return (
      <MultiSelect
        value={options.value}
        options={documentTypeOptions}
        onChange={(e) => options.filterCallback(e.value)}
        placeholder="Any"
        className="p-column-filter"
      />
    )
  }

  return (
    <div className="layout-with-sidebar">
      <div className="sidebar">
        <StaffSidebar />
      </div>
      <div className="main-content">
        <StaffProfileBox />
        <h1 className={styles.title}>Staff Documents</h1>
        {(allowedRoles.includes(userCtx.session.user.staff_role) ||
          isSupervisor) && (
          <Link to="/form/document_record_staff/1" className="btn">
            Add New Document
          </Link>
        )}
        <Toast ref={toast} position="bottom-right" />
        <DataTable
          value={data}
          responsiveLayout="scroll"
          filters={filters}
          sortField="date"
          sortOrder={-1}
        >
          <Column
            field="date"
            header="Date Uploaded"
            sortable
            body={dateBodyTemplate}
          />
          <Column field="location" header="Location" sortable />
          <Column field="document_title" header="Document Title" sortable />
          <Column
            field="document_type"
            header="Document Type"
            sortable
            filterElement={docTypeFilterTemplate}
            showFilterMenuOptions={false}
          />
          <Column field="created_by" header="Created By" sortable />
          <Column field="edit" header="Actions" />
        </DataTable>
      </div>
    </div>
  )
}
